import authStorage from "src/utils/authStorage";
/**
 * SimulatorChannel
 * Handles real-time simulator chat updates and token usage via ActionCable
 */
class SimulatorChannel {
  constructor(core) {
    this.core = core;
    this.subscription = null;
    this.handlers = {};
    this.partnerId = null;
    this.sessionId = null;
  }

  /**
   * Connect to the SimulatorChannel
   * @param {string} partnerId - The partner ID
   * @param {string} sessionId - The simulator session ID (optional)
   * @param {string|null} assistantId - The assistant ID (optional, scopes broadcasts to a specific assistant)
   * @param {Object} handlers - Event handlers for simulator updates
   * @returns {Object|null} The subscription object or null if connection failed
   */
  connect(partnerId, sessionId = null, assistantId = null, handlers = {}) {
    // Verificar se o WebSocket está inicializado
    if (!this.core || !this.core.initialized || !this.core.cable) {
      console.warn('WEBSOCKET WARNING: Cannot connect to SimulatorChannel - WebSocket not initialized');
      return null;
    }

    // Disconnect existing subscription if any
    if (this.subscription) {
      this.disconnect();
    }

    this.partnerId = partnerId;
    this.sessionId = sessionId;
    this.assistantId = assistantId || null;
    this.handlers = handlers;

    console.log('SIMULATOR CHANNEL: Connecting to SimulatorChannel', { partnerId, sessionId, assistantId });

    const subscriptionParams = {
      channel: 'SimulatorChannel',
      partner_id: partnerId,
      session_id: sessionId,
      token: authStorage.getItem('@sacgpt:token')
    };

    // Include assistant_id when provided to scope broadcasts
    if (assistantId) {
      subscriptionParams.assistant_id = String(assistantId);
    }

    try {
      // Subscribe to the channel
      // O consumer é na verdade this.core.cable
      this.subscription = this.core.cable.subscriptions.create(
        subscriptionParams,
        {
          // Called when subscription is ready
          connected: () => {
            console.log('SIMULATOR CHANNEL: Connected successfully');
            if (handlers.onConnected) {
              handlers.onConnected();
            }
          },

          // Called when subscription is terminated
          disconnected: () => {
            console.log('SIMULATOR CHANNEL: Disconnected');
            if (handlers.onDisconnected) {
              handlers.onDisconnected();
            }
          },

          // Called when data is received
          received: (data) => {
            console.log('SIMULATOR CHANNEL: Received data', data);
            this.handleMessage(data);
          },

          // Custom method to send a message
          sendMessage: function(message) {
            return this.perform('send_message', { message: message });
          },

          // Custom method to reset conversation
          resetConversation: function() {
            return this.perform('reset_conversation');
          },

          // Custom method to request current state
          requestState: function() {
            return this.perform('request_state');
          }
        }
      );

      // Request initial state after connecting
      setTimeout(() => {
        if (this.subscription && this.subscription.requestState) {
          this.subscription.requestState();
        }
      }, 500);

      return this.subscription;
    } catch (error) {
      console.error('SIMULATOR CHANNEL ERROR: Failed to connect', error);
      return null;
    }
  }

  /**
   * Disconnect from the SimulatorChannel
   */
  disconnect() {
    if (this.subscription && this.core.cable) {
      console.log('SIMULATOR CHANNEL: Disconnecting');
      this.core.cable.subscriptions.remove(this.subscription);
      this.subscription = null;
      this.handlers = {};
      this.partnerId = null;
      this.sessionId = null;
      this.assistantId = null;
    }
  }

  /**
   * Handle incoming messages
   * @param {Object} data - The message data
   */
  handleMessage(data) {
    const { event, payload } = data;

    switch (event) {
      case 'message_received':
        // New message from bot
        if (this.handlers.onMessageReceived) {
          this.handlers.onMessageReceived(payload);
        }
        break;

      case 'message_sent':
        // Confirmation of sent message
        if (this.handlers.onMessageSent) {
          this.handlers.onMessageSent(payload);
        }
        break;

      case 'typing_started':
        // Bot started typing
        if (this.handlers.onTypingStarted) {
          this.handlers.onTypingStarted(payload);
        }
        break;

      case 'typing_stopped':
        // Bot stopped typing
        if (this.handlers.onTypingStopped) {
          this.handlers.onTypingStopped(payload);
        }
        break;

      case 'conversation_reset':
        // Conversation was reset
        if (this.handlers.onConversationReset) {
          this.handlers.onConversationReset(payload);
        }
        break;

      case 'token_usage_updated':
        // Token usage was updated
        if (this.handlers.onTokenUsageUpdated) {
          this.handlers.onTokenUsageUpdated(payload);
        }
        break;

      case 'current_state':
        // Response to requestState
        if (this.handlers.onStateReceived) {
          this.handlers.onStateReceived(payload);
        }
        break;

      case 'error':
        console.error('SIMULATOR CHANNEL ERROR:', payload);
        if (this.handlers.onError) {
          this.handlers.onError(payload);
        }
        break;

      default:
        console.log('SIMULATOR CHANNEL: Unknown event', event, payload);
    }
  }

  /**
   * Send a message through the channel
   * @param {string} message - The message to send
   */
  sendMessage(message) {
    if (this.subscription && this.subscription.sendMessage) {
      this.subscription.sendMessage(message);
    } else {
      console.warn('SIMULATOR CHANNEL WARNING: Cannot send message - not connected');
    }
  }

  /**
   * Reset the conversation
   */
  resetConversation() {
    if (this.subscription && this.subscription.resetConversation) {
      this.subscription.resetConversation();
    } else {
      console.warn('SIMULATOR CHANNEL WARNING: Cannot reset conversation - not connected');
    }
  }

  /**
   * Request the current state
   */
  requestState() {
    if (this.subscription && this.subscription.requestState) {
      this.subscription.requestState();
    } else {
      console.warn('SIMULATOR CHANNEL WARNING: Cannot request state - not connected');
    }
  }

  /**
   * Check if connected
   * @returns {boolean} True if connected
   */
  isConnected() {
    return !!this.subscription;
  }
}

export default SimulatorChannel;